import { useState } from 'react';
import {
    Brain,
    Sparkles,
    Target,
    Users,
    Globe,
    BarChart3,
    Mail,
    Building,
    Search,
    Scale,
    Network,
    Medal,
    Flag
} from 'lucide-react';

const scoringCriteria = {
    companySize: {
        enterprise: 25,
        midMarket: 20,
        smb: 15,
        startup: 10
    },
    industryFit: {
        perfect: 25,
        high: 20,
        medium: 15,
        low: 5
    },
    ipActivity: {
        veryActive: 25,
        active: 20,
        moderate: 15,
        low: 5
    },
    marketPresence: {
        global: 15,
        regional: 10,
        local: 5
    },
    techStack: {
        enterprise: 10,
        midTier: 7,
        basic: 5
    }
};

// VMC (Voluntary Metadata Contacts) Criteria
const vmcCriteria = {
    dataQuality: {
        verified: 25,
        partial: 15,
        unverified: 5
    },
    metadataCompleteness: {
        complete: 25,
        partial: 15,
        minimal: 5
    },
    updateFrequency: {
        regular: 20,
        occasional: 10,
        rare: 5
    }
};

// AI Email Templates
const emailTemplates: any = {
    renewals: {
        subject: "Protecting Your IP Portfolio's Future",
        template: "Dear {{name}},\n\nI noticed that {{company}} has {{count}} trademarks approaching renewal in the next {{timeframe}}. Our AI analysis suggests these assets are crucial to your {{industry}} operations.\n\nWould you be interested in discussing our streamlined renewal process?"
    },
    vmc: {
        subject: "Enhancing Your IP Data Quality",
        template: "Dear {{name}},\n\nOur analysis shows that {{company}} could benefit from enhanced IP metadata management. We've identified specific areas where data enrichment could improve your portfolio's visibility and management efficiency."
    },
    highValueTarget: {
        subject: "Strategic IP Partnership Opportunity",
        template: "Dear {{name}},\n\nBased on {{company}}'s recent expansion in {{market}} and your growing IP portfolio of {{portfolioSize}} assets, I wanted to reach out regarding a strategic IP management approach."
    }
};

// Mock enriched company data
const mockEnrichedData = {
    companyProfile: {
        name: "TechCorp Solutions",
        industry: "Enterprise Software",
        size: "Enterprise",
        revenue: "$500M-1B",
        locations: ["UK", "EU", "US"],
        ipPortfolio: {
            trademarks: 245,
            patents: 180,
            domains: 67
        }
    },
    marketInsights: {
        totalAddressableMarket: "£12.4B",
        marketShare: "3.2%",
        growthRate: "18% YoY",
        competitiveLandscape: "Medium Density"
    },
    similarCompanies: [
        {
            name: "InnovateHub",
            matchScore: 92,
            ipPortfolio: 156,
            market: "UK/EU"
        },
        {
            name: "DataSphere Ltd",
            matchScore: 88,
            ipPortfolio: 134,
            market: "UK"
        }
    ],
    competitors: [
        {
            name: "Enterprise Logic",
            portfolioSize: 312,
            marketOverlap: "85%",
            regions: ["UK", "EU"]
        }
    ]
};

const Main = () => {
    const [leadScore, setLeadScore] = useState(null);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [generatedEmail, setGeneratedEmail] = useState("");

    const calculateLeadScore = (company: any) => {
        const baseScore =
            scoringCriteria.companySize.enterprise +
            scoringCriteria.industryFit.perfect +
            scoringCriteria.ipActivity.veryActive;

        const vmcScore =
            vmcCriteria.dataQuality.verified +
            vmcCriteria.metadataCompleteness.complete;

        return {
            total: baseScore + vmcScore,
            breakdown: {
                fitScore: baseScore,
                vmcScore: vmcScore,
                potential: "High"
            }
        };
    };

    const generateAIEmail = (company: any, template: any) => {
        // Simulate AI email generation using templates
        const emailContent = emailTemplates[template].template
            .replace("{{name}}", "John Smith")
            .replace("{{company}}", company.name)
            .replace("{{industry}}", company.industry);

        setGeneratedEmail(emailContent);
    };
    return (
        <div className="space-y-6">
            {/* Lead Scoring Card */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <Scale className="w-5 h-5" />
                        <span className='font-bold'> Lead Scoring Intelligence</span>
                    </div>
                    <div>
                        AI-powered lead scoring and enrichment
                    </div>
                </div>
                <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Scoring Results */}
                        <div className="space-y-4">
                            <div className="p-4 border rounded-lg">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="font-medium">Quality Score</h3>
                                    <div className="text-lg font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800">{mockEnrichedData.companyProfile.ipPortfolio.trademarks}/100</div>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span>Market Fit</span>
                                        <span>92%</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span>IP Activity</span>
                                        <span>88%</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span>VMC Quality</span>
                                        <span>95%</span>
                                    </div>
                                </div>
                            </div>

                            <div className="p-4 border rounded-lg">
                                <h3 className="font-medium mb-2">Market Insights</h3>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span>TAM</span>
                                        <span>{mockEnrichedData.marketInsights.totalAddressableMarket}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Growth Rate</span>
                                        <span>{mockEnrichedData.marketInsights.growthRate}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>Market Share</span>
                                        <span>{mockEnrichedData.marketInsights.marketShare}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Company Enrichment */}
                        <div className="space-y-4">
                            <div className="p-4 border rounded-lg">
                                <h3 className="font-medium mb-2">Similar Companies</h3>
                                <div className="space-y-2">
                                    {mockEnrichedData.similarCompanies.map((company, index) => (
                                        <div key={index} className="flex justify-between items-center">
                                            <span className="text-sm">{company.name}</span>
                                            <div className="text-lg text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm bg-blue-100 text-blue-800">{company.matchScore}% Match</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="p-4 border rounded-lg">
                                <h3 className="font-medium mb-2">Competitor Analysis</h3>
                                <div className="space-y-2">
                                    {mockEnrichedData.competitors.map((competitor, index) => (
                                        <div key={index} className="text-sm">
                                            <div className="flex justify-between">
                                                <span>{competitor.name}</span>
                                                <span>{competitor.marketOverlap} overlap</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* AI Playbook Generation */}
            <div className='bg-white p-[25px] rounded-[5px] shadow-2xl'>
                <div>
                    <div className="flex items-center gap-2">
                        <Brain className="w-5 h-5" />
                        <span className='font-bold'> AI Engagement Playbook</span>
                    </div>
                </div>
                <div>
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <h3 className="font-medium">Recommended Approach</h3>
                                <div className="p-4 border rounded-lg space-y-2">
                                    <div className="flex items-center gap-2">
                                        <Medal className="w-4 h-4 text-blue-500" />
                                        <span>High-value target - Enterprise approach</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Flag className="w-4 h-4 text-blue-500" />
                                        <span>Multi-region IP portfolio opportunity</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Network className="w-4 h-4 text-blue-500" />
                                        <span>Data enrichment potential</span>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <h3 className="font-medium">Email Generator</h3>

                                <select onChange={(e) => generateAIEmail(mockEnrichedData.companyProfile, e.target.value)} className="w-full focus:outline-none rounded-2xl py-2 px-4 border-4 border-main text-primary">
                                    <option value="renewals">Renewal Notice</option>
                                    <option value="vmc">VMC Enhancement</option>
                                    <option value="highValueTarget">Strategic Partnership</option>
                                </select>
                                {generatedEmail && (
                                    <div className="p-4 border rounded-lg mt-2">
                                        <pre className="text-sm whitespace-pre-wrap">{generatedEmail}</pre>
                                        <button className="flex items-center gap-2 mt-2 px-4 py-2 bg-green text-white rounded-lg">
                                            Customize & Send
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
export default Main